import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { baseURL } from '../constants/base.url';
import { IncomingCallEvent } from '../models/call';
import {
  ChatThread,
  ChatThreadListResponse,
  ChatMessage,
  ChatMessageListResponse,
  ApiResponse
} from '../models/message';
import { SocketService } from './socket.service';

export type UserRole = 'PATIENT' | 'SUPER_ADMIN' | 'CLINIC_ADMIN' | 'OPERATOR';

export interface ChatContact {
  _id: string;
  fullName: string;
  email?: string;
  phoneNo?: string;
  phone?: string;
  role?: string;
  isOnline?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  constructor(private http: HttpClient, private socketSvc: SocketService) { }

  // =========================
  // ✅ SAFE AUTH READERS (FIXED)
  // =========================

  private readJson(key: string): any | null {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null');
    } catch {
      return null;
    }
  }

  getCurrentRole(): UserRole | null {
    // 1) Direct role key (if you store it)
    const direct = localStorage.getItem('role') as UserRole | null;
    if (direct) return direct;

    // 2) Your actual login storage key
    const authUser = this.readJson('authenticatedUser');
    if (authUser?.role) return authUser.role;

    // 3) Fallback keys (safety)
    const keys = ['user', 'currentUser', 'authUser', 'profile', 'userRole'];
    for (const k of keys) {
      const obj = this.readJson(k);

      // userRole can be stored as string or object
      if (typeof obj === 'string') return obj as UserRole;
      if (obj?.role) return obj.role as UserRole;
    }

    return null;
  }

  getCurrentUserId(): string | null {
    // 1) Direct key (if you store it)
    const direct = localStorage.getItem('userId');
    if (direct) return direct;

    // 2) Your actual login storage key
    const authUser = this.readJson('authenticatedUser');
    if (authUser?._id) return authUser._id;
    if (authUser?.id) return authUser.id;

    // 3) Fallback keys
    const keys = ['user', 'currentUser', 'authUser', 'profile'];
    for (const k of keys) {
      const obj = this.readJson(k);
      if (obj?._id) return obj._id;
      if (obj?.id) return obj.id;
    }

    return null;
  }

  isStaff(): boolean {
    const r = this.getCurrentRole();
    return r === 'SUPER_ADMIN' || r === 'CLINIC_ADMIN' || r === 'OPERATOR';
  }

  isPatient(): boolean {
    return this.getCurrentRole() === 'PATIENT';
  }

  // =========================
  // THREAD APIs
  // =========================

  listThreads(query: {
    page?: number;
    limit?: number;
    status?: string;
    unread?: 'admin' | 'patient';
    search?: string;
  }): Observable<ChatThreadListResponse> {
    let params = new HttpParams();
    Object.entries(query || {}).forEach(([key, val]) => {
      if (val === undefined || val === null || val === '') return;
      params = params.set(key, String(val));
    });

    return this.http
      .get<ApiResponse<ChatThreadListResponse>>(`${baseURL}chat/threads`, { params })
      .pipe(map(res => res.data));
  }

  openThread(patientId: string): Observable<ChatThread> {
    return this.http
      .post<ApiResponse<{ thread: ChatThread }>>(`${baseURL}chat/threads`, { patientId })
      .pipe(map(res => res.data.thread));
  }

  toggleMute(threadId: string): Observable<ChatThread> {
    return this.http
      .post<ApiResponse<{ thread: ChatThread }>>(`${baseURL}chat/threads/${threadId}/mute`, {})
      .pipe(map(res => res.data.thread));
  }

  listMessages(
    threadId: string,
    query: { page?: number; limit?: number; before?: string }
  ): Observable<ChatMessageListResponse> {
    let params = new HttpParams();
    Object.entries(query || {}).forEach(([key, val]) => {
      if (val === undefined || val === null || val === '') return;
      params = params.set(key, String(val));
    });

    return this.http
      .get<ApiResponse<ChatMessageListResponse>>(`${baseURL}chat/threads/${threadId}/messages`, { params })
      .pipe(map(res => res.data));
  }

  sendMessage(
    threadId: string,
    text: string,
    targetUserId?: string | null,
    attachment?: File | null
  ): Observable<{ message: ChatMessage; thread: ChatThread }> {
    const formData = new FormData();

    formData.append('text', text || '');

    if (targetUserId) {
      formData.append('targetUserId', targetUserId);
    } else {
      formData.append('targetUserId', '');
    }

    if (attachment) {
      formData.append('attachment', attachment);
    }

    return this.http
      .post<ApiResponse<{ message: ChatMessage; thread: ChatThread }>>(
        `${baseURL}chat/threads/${threadId}/messages`,
        formData
      )
      .pipe(map(res => res.data));
  }

  markThreadRead(threadId: string): Observable<ChatThread> {
    return this.http
      .post<ApiResponse<{ thread: ChatThread }>>(`${baseURL}chat/threads/${threadId}/read`, {})
      .pipe(map(res => res.data.thread));
  }

  // ✅ Contacts API (PATIENT => staff users, STAFF => patients)
  getContacts(query: { q?: string; page?: number; limit?: number }): Observable<{ items: ChatContact[]; pagination: any }> {
    let params = new HttpParams();
    Object.entries(query || {}).forEach(([key, val]) => {
      if (val === undefined || val === null || val === '') return;
      params = params.set(key, String(val));
    });

    return this.http
      .get<ApiResponse<{ items: ChatContact[]; pagination: any }>>(`${baseURL}chat/contacts`, { params })
      .pipe(map(res => res.data));
  }

  // Chat React
  reactToMessage(messageId: string, reaction: string) {
    return this.http.post(`${baseURL}chat/messages/${messageId}/react`, {
      reaction
    });
  }

  // =========================
  // STAFF CONTACTS (Staff ↔ Staff chat)
  // =========================

  getStaffContacts(query: { q?: string; page?: number; limit?: number })
    : Observable<{ items: ChatContact[]; pagination: any }> {

    let params = new HttpParams();

    Object.entries(query || {}).forEach(([key, val]) => {
      if (val === undefined || val === null || val === '') return;
      params = params.set(key, String(val));
    });

    return this.http
      .get<ApiResponse<{ items: ChatContact[]; pagination: any }>>(
        `${baseURL}chat/staff/contacts`,
        { params }
      )
      .pipe(map(res => res.data));
  }


  // =========================
  // OPEN STAFF THREAD
  // =========================

  openStaffThread(userId: string): Observable<ChatThread> {

    return this.http
      .post<ApiResponse<{ thread: ChatThread }>>(
        `${baseURL}chat/staff/threads`,
        { targetUserId: userId }   // ✅ FIX
      )
      .pipe(map(res => res.data.thread));

  }

  // =========================
  // SOCKET HELPERS
  // =========================

  joinThreadRoom(threadId: string): void {
    this.socketSvc.emit('chat:thread:join', { threadId });
  }

  leaveThreadRoom(threadId: string): void {
    this.socketSvc.emit('chat:thread:leave', { threadId });
  }

  onMessageReaction(): Observable<{ messageId: string; reactions: any[] }> {
    return this.socketSvc.onEvent<{ messageId: string; reactions: any[] }>("chat:message:reaction");
  }

  onTyping() {
    return this.socketSvc.onEvent<{ threadId: string; userId: string; isTyping: boolean }>('chat:typing');
  }

  emitTyping(threadId: string, isTyping: boolean): void {
    this.socketSvc.emit('chat:typing', { threadId, isTyping });
  }

  onNewMessage() {
    return this.socketSvc.onEvent<{ threadId: string; message: ChatMessage; thread?: ChatThread }>('chat:message:new');
  }

  onUserOnline() {
    return this.socketSvc.onEvent('user-online');
  }

  onUserOffline() {
    return this.socketSvc.onEvent('user-offline');
  }

  // =========================
  // Call (Socket)
  // =========================

  onCallIncoming() { return this.socketSvc.onEvent<IncomingCallEvent>('call:incoming'); }
  onCallAccepted() { return this.socketSvc.onEvent<any>('call:accepted'); }
  onCallRejected() { return this.socketSvc.onEvent<any>('call:rejected'); }
  onCallEnded() { return this.socketSvc.onEvent<any>('call:ended'); }
  onCallOffer() { return this.socketSvc.onEvent<any>('call:offer'); }
  onCallAnswer() { return this.socketSvc.onEvent<any>('call:answer'); }
  onCallIce() { return this.socketSvc.onEvent<any>('call:ice'); }

  callJoin(callId: string) { this.socketSvc.emit('call:join', { callId }); }
  callLeave(callId: string) { this.socketSvc.emit('call:leave', { callId }); }
  callAccept(callId: string) { this.socketSvc.emit('call:accept', { callId }); }
  callReject(callId: string) { this.socketSvc.emit('call:reject', { callId }); }
  callEnd(callId: string, reason?: string) { this.socketSvc.emit('call:end', { callId, reason }); }
  callOffer(callId: string, sdp: any) { this.socketSvc.emit('call:offer', { callId, sdp }); }
  callAnswer(callId: string, sdp: any) { this.socketSvc.emit('call:answer', { callId, sdp }); }
  callIce(callId: string, candidate: any) { this.socketSvc.emit('call:ice', { callId, candidate }); }
}